/**
 * Analytics API Client
 * 
 * Phase 8.0: Analytics & Insights
 * Handles all analytics-related API calls to the backend.
 */

import { adminApiRequest } from "./admin-api-client";

// ============================================================================
// Types (from backend/src/types/analytics.ts)
// ============================================================================

/** Row when attributed sales are split by currency (no single combined total). */
export interface AttributedSalesBreakdownRow {
  currency: string;
  subtotal: string;
}

export interface OverviewMetrics {
  totalPlays: number;
  uniquePlayers: number;
  rewardsGranted: number;
  conversionRate: number;
  avgSessionDuration: number;
  rewardsRejected: number;
  attributedOrders: number;
  attributedRevenueSubtotal: string | null;
  attributedSalesCurrency: string | null;
  attributedSalesMixedCurrency: boolean;
  attributedSalesByCurrency?: AttributedSalesBreakdownRow[];
}

/** GET /admin/analytics/campaigns/:id — matches backend CampaignAnalytics. */
export interface CampaignMetrics {
  campaignId: string;
  campaignName: string;
  totalPlays: number;
  uniquePlayers: number;
  rewardsGranted: number;
  rewardsRejected: number;
  /** Distinct orders with a matched issued code for this campaign (orders/paid webhook). */
  attributedOrders: number;
  /** Sum of order subtotals when all redemptions share one currency; otherwise null. */
  attributedRevenueSubtotal: string | null;
  attributedSalesCurrency: string | null;
  attributedSalesMixedCurrency: boolean;
  attributedSalesByCurrency?: AttributedSalesBreakdownRow[];
  outcomes: { outcomeKey: string; label: string; count: number; percentage: number }[];
  timeline: {
    period: string;
    plays: number;
    rewards: number;
    uniquePlayers: number;
  }[];
}

/** GET /admin/analytics/campaigns/attribution-snapshot — batched rows for dashboard cards. */
export interface CampaignAttributionSnapshotRow {
  campaignId: string;
  attributedOrders: number;
  attributedRevenueSubtotal: string | null;
  attributedSalesCurrency: string | null;
  attributedSalesMixedCurrency: boolean;
  attributedSalesByCurrency?: AttributedSalesBreakdownRow[];
}

export interface TimeSeriesEntry {
  time: string;
  plays: number;
  rewards: number;
  conversionRate: number;
}

export type TimeSeriesData = TimeSeriesEntry[];

export type TimeSeriesGranularity = 'hour' | 'day' | 'week' | 'month';

/** Matches GET /admin/analytics/timeseries (range + groupBy query params). */
export interface AnalyticsTimeSeriesResponse {
  range: string;
  groupBy: string;
  data: Array<{
    period: string;
    plays: number;
    rewards: number;
    uniquePlayers: number;
  }>;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get analytics overview for the shop
 */
export async function getAnalyticsOverview(): Promise<OverviewMetrics> {
  return adminApiRequest<OverviewMetrics>("/admin/analytics/overview");
}

/**
 * Per-campaign attributed orders / sales for dashboard cards (single batched request).
 */
export async function getCampaignAttributionSnapshot(): Promise<
  CampaignAttributionSnapshotRow[]
> {
  return adminApiRequest<CampaignAttributionSnapshotRow[]>(
    "/admin/analytics/campaigns/attribution-snapshot"
  );
}

/**
 * Get detailed analytics for a specific campaign
 */
export async function getCampaignAnalytics(campaignId: string): Promise<CampaignMetrics> {
  return adminApiRequest<CampaignMetrics>(`/admin/analytics/campaigns/${campaignId}`);
}

/**
 * Get time-series data for plays and rewards
 * @deprecated Prefer {@link getAnalyticsTimeSeries} — backend uses `range` and `groupBy`.
 */
export async function getTimeSeriesData(
  granularity: TimeSeriesGranularity = 'day',
  days: number = 30
): Promise<TimeSeriesData> {
  return adminApiRequest<TimeSeriesData>(
    `/admin/analytics/timeseries?granularity=${granularity}&days=${days}`
  );
}

/**
 * Time-bucketed plays/rewards (e.g. last 7 days when range=7d).
 */
export async function getAnalyticsTimeSeries(
  range: "7d" | "30d" | "90d" | "all" = "7d",
  groupBy: "day" | "week" | "month" = "day"
): Promise<AnalyticsTimeSeriesResponse> {
  const q = new URLSearchParams({ range, groupBy });
  return adminApiRequest<AnalyticsTimeSeriesResponse>(
    `/admin/analytics/timeseries?${q.toString()}`
  );
}
